import { AsyncButton, StatusSnackbar, useShowSnackbar } from '@c42/component-library';
import { AsyncState, isError, isLoading } from '@c42/constants';
import {
  Autocomplete,
  AutocompleteValue,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Link as MuiLink,
  Select,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Link } from 'react-router-dom';
import Text from '../../../shared/Text';
import c42Jwt from '../../constants/c42Jwt';
import { SUPPORT_LOGIN_AUDIT_INDEX } from '../../constants/routes';
import { performRedirect } from '../../utils/performRedirect';
import FormValidationMessage from '../form-validation-message';
import LoginWrapper from '../login-wrapper';
import { useRoutingData } from '../providers/routing/hooks';
import { FormFieldWrapper, FormSubmitWrapper, FormWrapper } from '../shared-form-components';
import { useEmployeeInfo } from './useEmployeeInfo';
import { useTenantInfo } from './useTenantInfo';

type TenantOption = {
  label: string;
  id: string;
};

const SupportLoginForm: React.FC = () => {
  const [accessType, setAccessType] = React.useState('');
  const [submitState, setSubmitState] = React.useState(AsyncState.NOT_STARTED);
  const [copyBearerTokenState, setCopyBearerTokenState] = React.useState(AsyncState.NOT_STARTED);
  const [tenantIdOrX1AccountCode, setTenantIdOrX1AccountCode] = React.useState('');

  const theme = useTheme();
  const { employeeInfo, loadingState: employeeInfoState } = useEmployeeInfo();
  const { loadingState: tenantInfoLoadingState, tenantInfo } = useTenantInfo(tenantIdOrX1AccountCode);
  const { redirectParam } = useRoutingData();
  const showSnackbar = useShowSnackbar();

  const submitFormHandler = async (e?: React.FormEvent) => {
    if (e && 'preventDefault' in e) {
      e.preventDefault();
    }
    try {
      setSubmitState(AsyncState.LOADING);
      await c42Jwt.loginSupportUser({
        accessType,
        tenantIdOrX1AccountCode,
      });
      performRedirect(redirectParam);
    } catch {
      setSubmitState(AsyncState.ERROR);
    }
  };

  const changeTenantIdHandler = (
    _e: React.FormEvent,
    value: AutocompleteValue<TenantOption, false, boolean, boolean>,
  ) => {
    setTenantIdOrX1AccountCode(typeof value === 'string' ? value : value?.id);
  };

  const changeAccessTypeHandler: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setAccessType(event.target.value);
  };

  const copyBearerToken = async () => {
    try {
      setCopyBearerTokenState(AsyncState.LOADING);
      const response = await c42Jwt.getSupportUserSessionTokens({
        accessType,
        tenantIdOrX1AccountCode,
      });
      navigator.clipboard.writeText(response.accessToken.tokenValue);
      setCopyBearerTokenState(AsyncState.DONE);
      showSnackbar(<StatusSnackbar message={Text.get('copy_to_clipboard_success')} type="success" />);
    } catch {
      setCopyBearerTokenState(AsyncState.ERROR);
    }
  };

  const copySessionToken = () => {
    navigator.clipboard.writeText(employeeInfo?.sessionId);
    showSnackbar(<StatusSnackbar message={Text.get('copy_to_clipboard_success')} type="success" />);
  };

  const isFormDisabled = !employeeInfo;
  const isSupportAccessDisabled = tenantInfo && !tenantInfo.supportAccessEnabled;

  let error: string;
  if (isError(employeeInfoState)) {
    error = Text.get('support_login_employee_info_error');
  } else if (isError(submitState)) {
    error = Text.get('support_login_jwt_error');
  } else if (isError(tenantInfoLoadingState)) {
    error = Text.get('support_login_tenant_info_error');
  } else if (isSupportAccessDisabled) {
    error = Text.get('support_login_support_access_disabled');
  } else if (isError(copyBearerTokenState)) {
    error = Text.get('support_login_copy_bearer_token_error');
  }

  const recentlyUsedTenants: TenantOption[] =
    employeeInfo?.recentTenants?.map((tenant) => ({
      label: tenant.tenantName,
      id: tenant.tenantId,
    })) || [];

  return (
    <LoginWrapper
      footer={
        employeeInfo ? (
          <MuiLink component="button" onClick={copySessionToken}>
            {Text.get('support_login_copy_sts_session_token')}
          </MuiLink>
        ) : undefined
      }
    >
      <FormWrapper>
        <form name="supportLoginForm" noValidate onSubmit={submitFormHandler}>
          <FormValidationMessage message={error} />

          <Typography variant="h5">{Text.get('support_login_email')}</Typography>

          <Box ml={2}>
            <Typography>{employeeInfo?.email}</Typography>
          </Box>

          <FormFieldWrapper>
            <Autocomplete
              disabled={isFormDisabled}
              forcePopupIcon={recentlyUsedTenants.length > 0}
              freeSolo
              fullWidth
              onChange={changeTenantIdHandler}
              onInputChange={changeTenantIdHandler}
              options={recentlyUsedTenants}
              value={tenantIdOrX1AccountCode || ''}
              renderInput={(params) => (
                <TextField {...params} autoComplete="off" label={Text.get('support_login_tenant_id_label')} />
              )}
            />
          </FormFieldWrapper>

          {(tenantInfo || isLoading(tenantInfoLoadingState)) && (
            <Box display="flex" gap={0.5} ml={2} mt={2}>
              <Typography whiteSpace="nowrap">
                <strong>{Text.get('support_login_tenant_name_label')}: </strong>
              </Typography>
              {tenantInfo && <Typography sx={{ wordBreak: 'break-word' }}>{tenantInfo.name}</Typography>}
              {!tenantInfo && isLoading(tenantInfoLoadingState) && (
                <Skeleton height={theme.typography.body1.lineHeight} variant="rectangular" width="100%" />
              )}
            </Box>
          )}

          <FormFieldWrapper>
            <FormControl fullWidth>
              <InputLabel id="access-type-label">{Text.get('support_login_access_type_label')}</InputLabel>
              <Select
                disabled={isFormDisabled}
                fullWidth
                label={Text.get('support_login_access_type_label')}
                labelId="access-type-label"
                onChange={changeAccessTypeHandler}
                value={accessType || ''}
              >
                {employeeInfo?.groups?.map((group) => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormFieldWrapper>

          <FormSubmitWrapper gap={2}>
            <AsyncButton
              disabled={
                isLoading(copyBearerTokenState) ||
                isFormDisabled ||
                isSupportAccessDisabled ||
                !tenantIdOrX1AccountCode ||
                !accessType
              }
              isLoading={isLoading(copyBearerTokenState)}
              onClick={copyBearerToken}
              variant="outlined"
            >
              {Text.get('support_login_copy_bearer_token')}
            </AsyncButton>
            <AsyncButton
              data-test="submit"
              disabled={isLoading(submitState) || isFormDisabled || isSupportAccessDisabled || !accessType}
              isLoading={isLoading(submitState)}
              type="submit"
              variant="contained"
            >
              {Text.get('login_button_sign_in')}
            </AsyncButton>
          </FormSubmitWrapper>
        </form>
        <Box display="flex" justifyContent="center" mt={1}>
          <Typography color="primary" variant="body2">
            <Link to={SUPPORT_LOGIN_AUDIT_INDEX}>{Text.get('support_login_view_audit_log')}</Link>
          </Typography>
        </Box>
      </FormWrapper>
    </LoginWrapper>
  );
};

export default SupportLoginForm;
